import React, { useCallback, useRef, useState } from "react";
import classNames from "classnames";
import { useQueryClient } from "@tanstack/react-query";
import {
    counterpartiesQueryKeys,
    fetchCounterpartyDetails,
} from "@api/counterparty.api";
import { CounterpartyDetails } from "@common/types/counterpartyDetails";
import { CounterpartyAddress } from "@common/schemas/counterpartyAddress.schema";
import { CounterpartyContact } from "@common/schemas/counterpartyContact.schema";
import { Counterparty } from "@common/types/counterparty";
import {
    CounterpartyCombobox,
    CounterpartyComboboxValue,
} from "@components/transactions/TransactionSplitV2/CounterpartyCombobox";
import type { CounterpartyOcrPrefill } from "@components/accrualDocumentImporter/buildCounterpartyPrefillFromPartyHints";
import { CounterpartyEditorDrawer } from "@components/settings/Directory/CounterpartyEditor/CounterpartyEditorDrawer";
import { type CounterpartyFormFields } from "@components/counterparty/CounterpartyForm";
import { useCounterpartyVerifiedToast } from "@hooks/useCounterpartyVerifiedToast";
import { Button } from "@design-system/Button/Button";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { CheckIcon } from "@icons";
import { Field } from "@design-system/Field/Field";
import { Input } from "@design-system/Input/Input";
import { useScope } from "@state/scopeContext.lib";
import { AllFieldsToggle } from "./AllFieldsToggle";
import {
    SelectCustomerBooksModal,
    SelectedCustomerBooks,
} from "./SelectCustomerBooksModal";
import { toContactOption } from "./contactOption";
import { InvoiceEditCounterpartyModal } from "./InvoiceEditCounterpartyModal";
import { InvoicePartyAddressFields } from "./InvoicePartyAddressFields";
import {
    customerBooksFromDetails,
    type SelectNativeInvoiceCustomer,
} from "./copyNativeInvoicePartyDetails";
import { applyCustomerPatch } from "./nativeInvoiceFormHelpers";
import { NativeInvoiceCustomer } from "./nativeInvoiceFormTypes";
import { useNativeInvoiceFormContext } from "./NativeInvoiceFormContext";
import styles from "./SidePanel.module.scss";

interface Props {
    // The form hook's customer-change handler; it re-derives the To contact
    // fields when a different counterparty is picked, so the Details step must
    // not bypass it.
    onSelectCustomer: SelectNativeInvoiceCustomer;
    partySubtitle?: string;
    customerPlaceholder?: string;
    suggestedCustomerName?: string | null;
    counterpartyCreatePrefill?: CounterpartyOcrPrefill | null;
    isCreateCustomerOpen?: boolean;
    onCreateCustomerOpenChange?: (isOpen: boolean) => void;
    counterpartyCreateFields?: CounterpartyFormFields;
    counterpartyCreateDirection?: "left" | "right";
    onCustomerCreated?: (counterparty: Counterparty) => void;
    isCustomerReviewHighlighted?: boolean;
    customerAction?: React.ReactNode;
}

interface CustomerBooksPicker {
    customer: CounterpartyComboboxValue;
    contacts: CounterpartyContact[];
    addresses: CounterpartyAddress[];
}

function counterpartyIdentity(
    counterparty: Pick<
        Counterparty,
        "id" | "name" | "companyName" | "cc" | "bcc"
    >,
): CounterpartyComboboxValue {
    return {
        id: counterparty.id,
        name: counterparty.name,
        companyName: counterparty.companyName ?? undefined,
        cc: counterparty.cc,
        bcc: counterparty.bcc,
    };
}

// The "To" (customer) block of the Details step: counterparty picker (with
// the customer's email shown inline), plus contact fields (email, contact
// name, address, phone), and the edit-counterparty modal reachable from the
// picker.
// A field prints on the invoice whenever it has a value.
export const DetailsToFields: React.FC<Props> = ({
    onSelectCustomer,
    partySubtitle = "To",
    customerPlaceholder = "Select customer",
    suggestedCustomerName,
    counterpartyCreatePrefill,
    isCreateCustomerOpen: isCreateCustomerOpenProp,
    onCreateCustomerOpenChange,
    counterpartyCreateFields = "company",
    counterpartyCreateDirection = "left",
    onCustomerCreated,
    isCustomerReviewHighlighted,
    customerAction,
}) => {
    const { workspaceId } = useScope();
    const queryClient = useQueryClient();
    const { state, setState } = useNativeInvoiceFormContext();
    const [showAllFields, setShowAllFields] = useState(false);
    const [isCreateCustomerOpenInternal, setIsCreateCustomerOpenInternal] =
        useState(false);
    const isCreateCustomerOpen =
        isCreateCustomerOpenProp ?? isCreateCustomerOpenInternal;
    const setIsCreateCustomerOpen =
        onCreateCustomerOpenChange ?? setIsCreateCustomerOpenInternal;
    const showCounterpartyVerifiedToast = useCounterpartyVerifiedToast({
        forTransactionEditing: true,
    });
    const customer = state.customer;
    // Editing resolves the full counterparty via a name-scoped directory search,
    // so a name is required for the edit modal to load anything.
    const canEditCustomer = Boolean(customer?.id) && Boolean(customer?.name);
    const [isEditingCustomer, setIsEditingCustomer] = useState(false);

    // Counterparties with more than one contact or address require an explicit
    // choice (via SelectCustomerBooksModal) before the selection is applied.
    const [booksPicker, setBooksPicker] = useState<CustomerBooksPicker | null>(
        null,
    );
    const [isBooksPickerOpen, setIsBooksPickerOpen] = useState(false);
    const selectRequestIdRef = useRef(0);

    const loadDetails = async (
        counterparty: CounterpartyComboboxValue,
    ): Promise<
        | { status: "stale" }
        | { status: "missing" }
        | { status: "ready"; details: CounterpartyDetails }
    > => {
        if (!workspaceId) {
            return { status: "missing" };
        }
        const requestId = ++selectRequestIdRef.current;
        try {
            const details = await queryClient.fetchQuery({
                queryKey: counterpartiesQueryKeys.byId(
                    workspaceId,
                    counterparty.id,
                ),
                queryFn: () =>
                    fetchCounterpartyDetails({
                        workspaceId,
                        counterpartyId: counterparty.id,
                    }),
            });
            if (requestId !== selectRequestIdRef.current) {
                return { status: "stale" };
            }
            return { status: "ready", details };
        } catch {
            if (requestId !== selectRequestIdRef.current) {
                return { status: "stale" };
            }
            return { status: "missing" };
        }
    };

    const handleCustomerChange = async (
        counterparty: CounterpartyComboboxValue | undefined,
    ) => {
        if (!counterparty) {
            selectRequestIdRef.current += 1;
            setIsBooksPickerOpen(false);
            setBooksPicker(null);
            onSelectCustomer(undefined);
            return;
        }
        const loaded = await loadDetails(counterparty);
        if (loaded.status === "stale") {
            return;
        }
        if (loaded.status === "missing") {
            setIsBooksPickerOpen(false);
            onSelectCustomer(counterparty);
            return;
        }
        if (
            loaded.details.contacts.length > 1 ||
            loaded.details.addresses.length > 1
        ) {
            setBooksPicker({
                customer: counterparty,
                contacts: loaded.details.contacts,
                addresses: loaded.details.addresses,
            });
            setIsBooksPickerOpen(true);
            return;
        }
        setIsBooksPickerOpen(false);
        onSelectCustomer(
            counterparty,
            customerBooksFromDetails(loaded.details),
        );
    };

    const selectCreatedCustomer = (counterparty: Counterparty) => {
        setIsCreateCustomerOpen(false);
        void handleCustomerChange(counterparty);
        onCustomerCreated?.(counterparty);
    };

    const handleBooksConfirm = (selection: SelectedCustomerBooks) => {
        if (!booksPicker) {
            return;
        }
        onSelectCustomer(booksPicker.customer, selection);
        setIsBooksPickerOpen(false);
    };

    const handleCounterpartyUpdated = async (updated: Counterparty) => {
        const counterparty = counterpartyIdentity(updated);
        const loaded = await loadDetails(counterparty);
        if (loaded.status === "stale") {
            return;
        }
        onSelectCustomer(
            counterparty,
            loaded.status === "ready"
                ? customerBooksFromDetails(loaded.details)
                : undefined,
        );
    };

    const updateCustomer = useCallback(
        (patch: Partial<NativeInvoiceCustomer>) =>
            setState((prev) => applyCustomerPatch({ prev, patch })),
        [setState],
    );

    const hasSelectedCustomer = Boolean(state.customer?.id);
    const unmatchedCustomerName =
        hasSelectedCustomer || !suggestedCustomerName
            ? undefined
            : suggestedCustomerName;
    const customerFieldLabel = customerPlaceholder.replace(/^Select /, "");
    const customerPicker = (
        <CounterpartyCombobox
            selectedCounterparty={
                state.customer?.id ? state.customer : undefined
            }
            onChange={(counterparty) => {
                void handleCustomerChange(counterparty);
            }}
            placeholder={customerPlaceholder}
            searchPlaceholder="Search customers..."
            createButtonText="Create new customer"
            emptyStateText="No matching customers"
            clearSelectionText="No customer"
            editButtonText="Edit customer"
            createModalTitle="Create customer"
            onCreateCounterparty={() => setIsCreateCustomerOpen(true)}
            toCounterpartyOption={toContactOption}
            renderSelectedCounterparty={(counterparty) => {
                if (!counterparty) {
                    return unmatchedCustomerName ? (
                        <span className={styles.suggestedCustomerName}>
                            {unmatchedCustomerName}
                        </span>
                    ) : null;
                }
                return (
                    <div className={styles.fromOption}>
                        <span className={styles.fromOptionName}>
                            {counterparty.name}
                        </span>
                        {counterparty.email && (
                            <span className={styles.fromOptionEmail}>
                                {counterparty.email}
                            </span>
                        )}
                    </div>
                );
            }}
            onEditCounterparty={
                canEditCustomer ? () => setIsEditingCustomer(true) : undefined
            }
            triggerButtonProps={{
                variant: "primary",
                size: "xl",
                block: true,
                "aria-label": "Customer",
                className: isCustomerReviewHighlighted
                    ? styles.reviewTaskBorderHighlight
                    : undefined,
            }}
        />
    );
    let customerControl = customerPicker;
    if (unmatchedCustomerName) {
        customerControl = (
            <div className={styles.suggestedCustomerRow}>
                {customerPicker}
                <Tooltip content={`Create ${unmatchedCustomerName}`}>
                    <Button
                        type="button"
                        variant="primary"
                        size="xl"
                        iconOnly
                        aria-label="Confirm"
                        onClick={() => setIsCreateCustomerOpen(true)}
                    >
                        <CheckIcon />
                    </Button>
                </Tooltip>
            </div>
        );
    } else if (customerAction) {
        customerControl = (
            <div className={styles.suggestedCustomerRow}>
                {customerPicker}
                {customerAction}
            </div>
        );
    }

    return (
        <>
            <div
                className={classNames(
                    styles.groupSubtitleRow,
                    styles.groupSubtitleRowTight,
                )}
            >
                <div className={styles.groupSubtitleSecondary}>
                    {partySubtitle}
                </div>
                <div className={styles.subtitleActionsCluster}>
                    <AllFieldsToggle
                        isActive={showAllFields}
                        onChanged={setShowAllFields}
                    />
                </div>
            </div>
            <div className={classNames(styles.fieldGrid, styles.fieldGridWide)}>
                {suggestedCustomerName !== undefined ? (
                    <Field label={customerFieldLabel} control="button">
                        {customerControl}
                    </Field>
                ) : (
                    <Field.Root>{customerControl}</Field.Root>
                )}
            </div>
            <CounterpartyEditorDrawer
                open={isCreateCustomerOpen}
                isCreating
                direction={counterpartyCreateDirection}
                onClose={() => setIsCreateCustomerOpen(false)}
                onCreated={(creationResponse) => {
                    showCounterpartyVerifiedToast(creationResponse);
                    selectCreatedCustomer(creationResponse.counterparty);
                }}
                onEdited={selectCreatedCustomer}
                onLocalized={selectCreatedCustomer}
                fields={counterpartyCreateFields}
                forTransactionEditing
                initialValues={
                    hasSelectedCustomer
                        ? undefined
                        : {
                              ...counterpartyCreatePrefill?.initialValues,
                              name:
                                  counterpartyCreatePrefill?.initialValues
                                      .name ??
                                  unmatchedCustomerName ??
                                  "",
                          }
                }
                seedContacts={
                    hasSelectedCustomer
                        ? undefined
                        : counterpartyCreatePrefill?.contacts
                }
                seedAddresses={
                    hasSelectedCustomer
                        ? undefined
                        : counterpartyCreatePrefill?.addresses
                }
            />
            {showAllFields && (
                <>
                    <div
                        className={classNames(
                            styles.fieldGrid,
                            styles.fieldGridWide,
                        )}
                    >
                        <Field label="Contact name">
                            <Input
                                size="xl"
                                maxLength={255}
                                value={state.toFullName}
                                onChange={(event) =>
                                    setState((prev) => ({
                                        ...prev,
                                        toFullName: event.target.value,
                                    }))
                                }
                            />
                        </Field>
                        <Field label="Company">
                            <Input
                                size="xl"
                                maxLength={255}
                                value={state.toCompany}
                                onChange={(event) =>
                                    setState((prev) => ({
                                        ...prev,
                                        toCompany: event.target.value,
                                    }))
                                }
                            />
                        </Field>
                        <Field label="Email">
                            <Input
                                size="xl"
                                type="email"
                                maxLength={255}
                                value={customer?.email ?? ""}
                                onChange={(event) =>
                                    updateCustomer({
                                        email: event.target.value,
                                    })
                                }
                            />
                        </Field>
                    </div>
                    <InvoicePartyAddressFields
                        values={{
                            country: customer?.country ?? "",
                            street: customer?.street ?? "",
                            street2: customer?.street2 ?? "",
                            city: customer?.city ?? "",
                            state: customer?.state ?? "",
                            postalCode: customer?.postalCode ?? "",
                            phoneNumber: customer?.phoneNumber ?? "",
                        }}
                        onChange={updateCustomer}
                    />
                </>
            )}
            {booksPicker ? (
                <SelectCustomerBooksModal
                    key={booksPicker.customer.id}
                    show={isBooksPickerOpen}
                    customerName={booksPicker.customer.name}
                    contacts={booksPicker.contacts}
                    addresses={booksPicker.addresses}
                    onConfirm={handleBooksConfirm}
                    onClose={() => setIsBooksPickerOpen(false)}
                />
            ) : null}
            {canEditCustomer && customer ? (
                <InvoiceEditCounterpartyModal
                    show={isEditingCustomer}
                    onClose={() => setIsEditingCustomer(false)}
                    counterpartyId={customer.id}
                    onCounterpartyUpdated={(counterparty) => {
                        void handleCounterpartyUpdated(counterparty);
                    }}
                />
            ) : null}
        </>
    );
};
